import { useState } from 'react';

import type { Currency } from '../../entities/Currency';
import type { PaymentMethodType } from '../../entities/enums';
import useTimer from '../../hook/useTimer';
import {
  redirectEWalletQuery,
  redirectOnlineBankingQuery,
  redirectPayPalQuery,
} from '../../utils/constants';
import useFpxCheckStatus from '../Fpx/useProcessingPaymentFpx';

import useCheckStatus from './CheckStatus';
import useProcessingPayment2c2p from './ProcessingPayment2c2p';

export interface useFromThirdParty {
  currency: Currency;
  paymentMethod: PaymentMethodType;
  onComplete: ({ status, error }: { status?: string; error?: any }) => void;
}

const useFromThirdParty = ({
  currency,
  paymentMethod,
  onComplete,
}: useFromThirdParty) => {
  // state to show CheckButton or LoadingChecking component
  const [showCheckStatus, setShowCheckStatus] = useState<boolean>(false);

  useTimer({
    onTimeOver: () => setShowCheckStatus(true),
    delayInMs: 1000 * 30, // 30 seconds
  });

  const [isOnlineBankingReturn] = useProcessingPayment2c2p({
    currency,
    paymentMethod,
    redirectQuery: redirectOnlineBankingQuery,
    onComplete,
  });

  const [isEWalletReturn] = useProcessingPayment2c2p({
    currency,
    paymentMethod,
    redirectQuery: redirectEWalletQuery,
    onComplete,
  });

  const [isPayPalReturn] = useProcessingPayment2c2p({
    currency,
    paymentMethod,
    redirectQuery: redirectPayPalQuery,
    onComplete,
  });

  const { handleCheckButtonClick, isCheckPaymentInProgress } = useCheckStatus({
    currency,
    paymentMethod,
    onComplete,
  });

  const [isFPXReturn] = useFpxCheckStatus({
    onComplete,
  });

  const isFromThirdParty =
    isEWalletReturn || isPayPalReturn || isFPXReturn || isOnlineBankingReturn;

  return {
    showCheckStatus,
    isFromThirdParty,
    checkButton: {
      handleCheckButtonClick,
      isCheckPaymentInProgress,
    },
  };
};

export default useFromThirdParty;
